/**
 * React Query hook for Venues (Global)
 * Provides automatic caching, background sync, and error handling
 * Can be used across all modules
 */

import { useQuery } from '@tanstack/react-query';
import { getVenueList } from 'services/Settings';
import { totalCountEvent } from 'services/generalServices';
import { queryKeys } from '../config/queryClient';

/**
 * Fetches venues data
 * Always loads the full list by getting total count first, then fetching all items
 *
 * @param {Object} params - Query parameters
 * @param {number} params.page_limit - Limit for fetching venues
 * @param {string} params.pagination_token - Pagination token (default: '')
 * @param {string} params.pagination_direction - Pagination direction (default: 'forward')
 * @param {boolean} params.excludeFromLoading - Exclude from loading state (default: false)
 * @param {string} params.sort_order - Sort order (default: 'alphabet_ascending')
 * @param {string|Array} params.practiceAreaId - Practice area filter (default: '')
 * @returns {Promise<Array>} - Array of venues
 */
const fetchVenues = async ({
  page_limit = 10,
  pagination_token = '',
  pagination_direction = 'forward',
  excludeFromLoading = false,
  sort_order = 'alphabet_ascending',
  practiceAreaId = '',
}) => {
  try {
    const response = await getVenueList(page_limit, pagination_token, pagination_direction, excludeFromLoading, sort_order, practiceAreaId);

    return response?.data?.venues || [];
  } catch (error) {
    console.error('Error fetching venues:', error);
    throw error;
  }
};

/**
 * Hook to fetch venues with React Query
 * Always loads the full list by getting total count first (same as useVenueList)
 *
 * @param {Object} options - Configuration options
 * @param {number} options.page_limit - Limit for fetching venues (default: uses total count)
 * @param {string} options.pagination_token - Pagination token (default: '')
 * @param {string} options.pagination_direction - Pagination direction (default: 'forward')
 * @param {boolean} options.excludeFromLoading - Exclude from loading state (default: false)
 * @param {string} options.sort_order - Sort order (default: 'alphabet_ascending')
 * @param {string|Array} options.practiceAreaId - Practice area filter (default: '')
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 * @param {boolean} options.loadFullList - If true, gets total count first (default: true)
 *
 * @returns {Object} - venueOptions, isVenueLoading, error, refetch, isFetching
 */
export const useVenuesQuery = ({
  page_limit,
  pagination_token = '',
  pagination_direction = 'forward',
  excludeFromLoading = false,
  sort_order = 'alphabet_ascending',
  practiceAreaId = '',
  enabled = true,
  loadFullList = true,
} = {}) => {
  // Get total count first (same as useVenueList) - only if loadFullList is true and page_limit is not provided
  // This count is shared across ALL reports to ensure consistent cache keys
  const { data: countData, isLoading: isCountLoading } = useQuery({
    queryKey: ['venue-count'],
    queryFn: async () => {
      const countResponse = await totalCountEvent('venue');
      return countResponse?.data || 10;
    },
    enabled: enabled && loadFullList && !page_limit,
    staleTime: 30 * 60 * 1000, // Increased to 30 minutes - count rarely changes
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch when component mounts - use cache
    retry: 2,
  });

  // Determine the limit value
  // For loadFullList mode, wait for countData before proceeding
  const limit = page_limit || (loadFullList ? countData : 10) || 10;

  // For shared caching across reports, we need to ensure all reports use the same cache key
  // Only enable the query when limit is stable (countData is loaded for loadFullList mode)
  const isLimitStable = !!page_limit || !loadFullList || !!countData;

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: queryKeys.venues.list(limit, pagination_token, pagination_direction, sort_order, practiceAreaId),
    queryFn: () =>
      fetchVenues({
        page_limit: limit,
        pagination_token,
        pagination_direction,
        excludeFromLoading,
        sort_order,
        practiceAreaId,
      }),
    // Only fetch when enabled AND limit is stable to prevent cache key changes
    enabled: enabled && isLimitStable,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    // refetchOnMount is intentionally not set to false, allowing refetch of invalidated (stale) data
    // This ensures that when venues are created/updated in Settings, reports see the changes immediately
    retry: 2,
  });

  return {
    venueOptions: data || [],
    isVenueLoading: isLoading || (loadFullList && isCountLoading), // Include count loading state
    isFetching,
    error,
    refetch,
  };
};
